import _             from 'underscore';
import React, { FC } from 'react';

import { CorvalStaticProjectActivity, FuncOrAsyncFunc, SystemEvent, Uuid } from '@src/types';

import { useFeatureFlags, useSystemModals, useSystemToasts } from '@src/hooks';
import { runIfFunction }                                     from "@src/util";
import { UseIf }                                             from "@src/views/components/display/EqualValues";

import { StaticProjectActivityBulkUpdateNotes }                                                                                    from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateNotes";
import { StaticProjectActivityBulkUpdateTiming }                                                                                   from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateTiming";
import { StaticProjectActivityBulkUpdateHireType }                                                                                 from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateHireType";
import { StaticProjectActivityBulkUpdateOperationalCost }                                                                          from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateOperationalCost";
import { StaticProjectActivityBulkUpdateOngoingCost }                                                                              from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateOngoingCost";
import { StaticProjectActivityBulkUpdateCriticality }                                                                              from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateCriticality";
import { StaticProjectActivityBulkUpdateBusinessFunction }                                                                         from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateBusinessFunction";
import { StaticProjectActivityBulkUpdateCompletionState }                                                                          from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateCompletionState";
import { StaticProjectActivityBulkUpdateIsAtRisk }                                                                                 from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateIsAtRisk";
import { StaticStaticProjectActivityBulkUpdateDialogFormContainer, StaticStaticProjectActivityBulkUpdateDialogFormContainerProps } from "@src/views/pages/projects/activities/bulk/StaticProjectActivityBulkUpdateDialogFormContainer";


export type SupportedBulkUpdateFields =
    'notes'
    | 'timing'
    | 'isAtRisk'
    | 'hireType'
    | 'criticality'
    | 'completionState'
    | 'operationalCost'
    | 'ongoingCost'
    | 'businessFunction';

export const staticProjectActivityBulkUpdateFields: Record<SupportedBulkUpdateFields, string> = {
    timing:           'Timing',
    criticality:      'Priority',
    isAtRisk:         'At Risk',
    completionState:  'Status',
    businessFunction: 'Business Function',
    operationalCost:  'Operational Cost',
    ongoingCost:      'Ongoing Cost',
    hireType:         'Hire Types',
    notes:            'Notes',
}

export interface StaticProjectActivityBulkUpdateDialogProps {
    projectId: Uuid,
    projectVersionId: Uuid | null,
    projectActivityIds: Uuid[],
    updateType: keyof typeof staticProjectActivityBulkUpdateFields,
    title?: string,
    closeOnEntityUpdate?: boolean,
    onUpdateComplete: FuncOrAsyncFunc<[ mutatedActivitySet: Array<CorvalStaticProjectActivity> ], void>,
    onUpdateError: FuncOrAsyncFunc<[ e: Error ], void>,

    onUpdateCanceled?( e: SystemEvent ): void,
}

const defaultProps: StaticProjectActivityBulkUpdateDialogProps = {
    title:               null,
    updateType:          null,
    projectId:           null,
    projectVersionId:    null,
    projectActivityIds:  [],
    onUpdateComplete:    null,
    onUpdateError:       null,
    closeOnEntityUpdate: false,
}

const StaticProjectActivityBulkUpdateDialog: FC<StaticProjectActivityBulkUpdateDialogProps> = ( props ) => {

    let { projectId, projectVersionId, projectActivityIds } = props,
        featureFlags                                        = useFeatureFlags();

    const viewTypes = _.mapObject( staticProjectActivityBulkUpdateFields, ( v, k ) => k ) as Record<SupportedBulkUpdateFields, SupportedBulkUpdateFields>;

    const permissionHash: Map<keyof typeof viewTypes, boolean> = new Map( [
        [ viewTypes.timing, true ],
        [ viewTypes.criticality, true ],
        [ viewTypes.isAtRisk, true ],
        [ viewTypes.completionState, true ],
        [ viewTypes.businessFunction, true ],
        [ viewTypes.operationalCost, featureFlags.permissionProjectActivityUpdateOperationalCost ],
        [ viewTypes.ongoingCost, featureFlags.permissionProjectActivityUpdateOngoingCost ],
        [ viewTypes.hireType, featureFlags.permissionProjectActivityUpdateHireTypes ],
        [ viewTypes.notes, true ],
    ] );

    const dialogTitle   = props.title ?? `Bulk Update ${ staticProjectActivityBulkUpdateFields[ props.updateType ] }`,
          userCanUpdate = permissionHash.get( props.updateType );

    const bulkUpdaterProps = {
        projectId,
        projectVersionId,
        projectActivityIds,
        supportModals: true,
        supportToasts: true,
        formContainer: ( formContainerProps: StaticStaticProjectActivityBulkUpdateDialogFormContainerProps ) => (
            <StaticStaticProjectActivityBulkUpdateDialogFormContainer

                { ...formContainerProps }

                title={ dialogTitle }
                operationIsHidden={ !userCanUpdate }
                onComplete={ ( data ) => {
                    formContainerProps.onComplete( data );
                    runIfFunction( props.onUpdateComplete, data.updateProjectActivities );
                } }
                onError={ ( data ) => {
                    formContainerProps.onError( data );
                    runIfFunction( props.onUpdateError, data );
                } }
                closeOnEntityUpdate={ props.closeOnEntityUpdate }
            />
        )
    }

    return (

        <>
            <UseIf x={ props.updateType === viewTypes.notes }>
                <StaticProjectActivityBulkUpdateNotes { ...bulkUpdaterProps } />
            </UseIf>

            <UseIf x={ props.updateType === viewTypes.hireType }>
                <StaticProjectActivityBulkUpdateHireType { ...bulkUpdaterProps } />
            </UseIf>

            <UseIf x={ props.updateType === viewTypes.timing }>
                <StaticProjectActivityBulkUpdateTiming { ...bulkUpdaterProps } />
            </UseIf>

            <UseIf x={ props.updateType === viewTypes.operationalCost }>
                <StaticProjectActivityBulkUpdateOperationalCost { ...bulkUpdaterProps } />
            </UseIf>

            <UseIf x={ props.updateType === viewTypes.ongoingCost }>
                <StaticProjectActivityBulkUpdateOngoingCost { ...bulkUpdaterProps } />
            </UseIf>

            <UseIf x={ props.updateType === viewTypes.criticality }>
                <StaticProjectActivityBulkUpdateCriticality { ...bulkUpdaterProps } />
            </UseIf>

            <UseIf x={ props.updateType === viewTypes.businessFunction }>
                <StaticProjectActivityBulkUpdateBusinessFunction { ...bulkUpdaterProps } />
            </UseIf>

            <UseIf x={ props.updateType === viewTypes.completionState }>
                <StaticProjectActivityBulkUpdateCompletionState { ...bulkUpdaterProps } />
            </UseIf>

            <UseIf x={ props.updateType === viewTypes.isAtRisk }>
                <StaticProjectActivityBulkUpdateIsAtRisk { ...bulkUpdaterProps } />
            </UseIf>
        </>
    )
};

StaticProjectActivityBulkUpdateDialog.defaultProps = defaultProps;

export { StaticProjectActivityBulkUpdateDialog }
